import { Component } from '@theme/component';
import { isExactProductVariantIdentityResponse } from '@theme/discovery-authority-state';
import { ThemeEvents } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that displays a product SKU.
 * This component listens for variant update events and updates the SKU display accordingly.
 * It handles SKU updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} skuContainer - The container element for the SKU
 * @property {HTMLElement} sku - The span element that displays the SKU text
 *
 * @extends {Component<Refs>}
 */
class ProductSkuComponent extends Component {
  requiredRefs = ['skuContainer', 'sku'];

  #selectionGeneration = 0;

  /** @type {Element | null} */
  #eventTarget = null;

  connectedCallback() {
    super.connectedCallback();
    this.#eventTarget = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    this.#eventTarget?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#selectionGeneration += 1;
    this.#eventTarget?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#eventTarget = null;
  }

  /**
   * Handles product select event and updates the SKU.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element)) return;
    const generation = ++this.#selectionGeneration;
    const { variantId = '', connectedProductId = '' } = event.detail ?? {};
    const expectedProductId = String(connectedProductId || event.target.getAttribute('data-product-id') || '');
    const intendedVariantId = String(variantId || '');
    this.dataset.launchAllowed = 'false';
    delete this.dataset.approvedVariantId;
    this.hidden = true;
    this.style.display = 'none';
    this.refs.sku.textContent = '';

    event.promise
      .then(({ detail }) => {
        if (generation !== this.#selectionGeneration || !this.isConnected || !detail?.html) return;

        const { html, resource } = detail;
        const responseCarrier = [...html.querySelectorAll('[data-product-grid-content], product-card')].find(
          (carrier) => carrier.getAttribute('data-product-id')?.trim() === expectedProductId
        );
        const responseProductId = responseCarrier?.getAttribute('data-product-id')?.trim() ?? '';
        const approvedVariantId = responseCarrier?.getAttribute('data-approved-variant-id')?.trim() ?? '';
        const newSku = responseCarrier?.querySelector('product-sku-component');
        const serverAllowed = isExactProductVariantIdentityResponse({
          launchAllowed:
            responseCarrier?.getAttribute('data-launch-allowed') === 'true' &&
            newSku?.getAttribute('data-launch-allowed') === 'true' &&
            newSku?.getAttribute('data-approved-variant-id') === approvedVariantId,
          expectedProductId,
          responseProductId,
          intendedVariantId,
          approvedVariantId,
          resourceId: String(resource?.id ?? ''),
          available: resource?.available === true,
        });
        if (!serverAllowed || generation !== this.#selectionGeneration || !this.isConnected) return;

        const variantSku = resource.sku || '';
        this.dataset.productId = responseProductId;
        this.dataset.approvedVariantId = approvedVariantId;
        this.dataset.launchAllowed = 'true';
        this.refs.sku.textContent = variantSku;
        this.hidden = variantSku === '';
        this.style.display = variantSku === '' ? 'none' : 'block';
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-sku] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('product-sku-component')) {
  customElements.define('product-sku-component', ProductSkuComponent);
}
